import { Text } from "@solomon-labs/design/components/ui";
import { useInView, useReducedMotion } from "motion/react";
import React from "react";

import {
    LABEL_BOTTOM,
    LABEL_ROOM,
    STRIP_RAIL_TOP,
    STRIP_VIEWBOX_HEIGHT,
    STRIP_VIEWBOX_WIDTH
} from "./monitoringStrip";

// A schematic, not a diagram of deployed controls or an assessment result.
// It is drawn in the box the Blockaid strip uses (see monitoringStrip), so the
// two render at one scale and one height side by side.
// Four abstract control arrangements: branching approvals, bounded access,
// infrastructure layers, and release gates. Each carries the centres of its
// node squares, placed where they sit on its own line: the release gate runs
// bottom-left to top-right, so its nodes do too.
interface Schematic {
    path: string;
    nodes: readonly [x: number, y: number][];
}
const DIAGONAL_NODES: Schematic["nodes"] = [
    [-12, -12],
    [0, 0],
    [12, 12]
];
const SCHEMATICS: readonly Schematic[] = [
    { path: "M-12 -12H0V12H12M0 0H12", nodes: DIAGONAL_NODES },
    { path: "M-12 12V-12H12V12M-12 0H12", nodes: DIAGONAL_NODES },
    { path: "M-12 -12H12M-12 0H12M-12 12H12M0 -12V12", nodes: DIAGONAL_NODES },
    {
        path: "M-12 12V0H0V-12H12M0 0H12V12",
        nodes: [
            [12, -12],
            [0, 0],
            [-12, 12]
        ]
    }
];
const VIEWBOX_WIDTH = STRIP_VIEWBOX_WIDTH;
const PANEL_COUNT = SCHEMATICS.length;
// Four panels centred on the strip. The step is as wide as the frame's last
// stop allows: its top-right bracket reaches above the top rail, into the band
// where the "Illustration" label stands at the strip's right end, and must
// clear it at every width, the 12px label on a phone being the widest in units.
const PANEL_STEP = 56;
const PANEL_FIRST = VIEWBOX_WIDTH / 2 - ((PANEL_COUNT - 1) / 2) * PANEL_STEP;
const SLOT_MS = 1400;
const CYCLE_MS = SLOT_MS * (PANEL_COUNT + 1);
const HOLD_MS = 1000;
const percent = (ms: number): number => (ms / CYCLE_MS) * 100;
// The track runs at y 36 between rails at 12 and 60. The frame's brackets
// reach 32 below the track, and the shared box ends past them at 69.
const TRACK_Y = 36;
const RAIL_TOP_Y = STRIP_RAIL_TOP;
const RAIL_BOTTOM_Y = 60;
const VIEWBOX_HEIGHT = STRIP_VIEWBOX_HEIGHT;
// The inspection frame is a 64-unit square on the track. The probe is a
// hairline that spans it edge to edge and sweeps its full height; a stroke
// straddles its path, so the travel is inset by half a stroke at each end and
// the line stops flush inside the brackets, as the Blockaid sweep does.
const FRAME_HALF = 32;
const PROBE_STROKE = 1;
const PROBE_FROM = -FRAME_HALF + PROBE_STROKE / 2;
const PROBE_TO = FRAME_HALF - PROBE_STROKE / 2;
const FRAME_STOPS = Array.from({ length: PANEL_COUNT }, (_, index) => {
    const start = percent(index * SLOT_MS);
    const hold = percent(index * SLOT_MS + HOLD_MS);
    return `${start}%, ${hold}% { transform: translateX(${index * PANEL_STEP}px); opacity: 1; }`;
}).join("\n");

const INSPECTION_CSS = `
    .control-inspection { --inspection-cycle: ${CYCLE_MS}ms; }
    .control-inspection-frame {
        animation: control-inspection-frame var(--inspection-cycle) var(--ease-in-out-cubic) infinite;
    }
    .control-inspection-detail {
        opacity: 0;
        animation: control-inspection-detail var(--inspection-cycle) linear infinite;
    }
    .control-inspection-probe {
        animation: control-inspection-probe ${SLOT_MS}ms var(--ease-in-out-cubic) infinite;
    }
    .control-inspection-flash {
        opacity: 0;
        animation: control-inspection-flash ${SLOT_MS}ms var(--ease-out-cubic) infinite;
    }
    .control-inspection[data-motion="paused"] [data-inspection-motion] {
        animation-play-state: paused;
    }
    @keyframes control-inspection-frame {
        ${FRAME_STOPS}
        ${percent(PANEL_COUNT * SLOT_MS)}% { transform: translateX(${(PANEL_COUNT - 1) * PANEL_STEP}px); opacity: 0; }
        99% { transform: translateX(0); opacity: 0; }
        100% { transform: translateX(0); opacity: 1; }
    }
    @keyframes control-inspection-detail {
        0% { opacity: 0; }
        ${percent(150)}%, ${percent(HOLD_MS)}% { opacity: 1; }
        ${percent(SLOT_MS)}%, 100% { opacity: 0; }
    }
    @keyframes control-inspection-probe {
        0% { transform: translateY(${PROBE_FROM}px); opacity: 0; }
        12%, 38% { opacity: 0.45; }
        45%, 100% { transform: translateY(${PROBE_TO}px); opacity: 0; }
    }
    @keyframes control-inspection-flash {
        0%, 48% { opacity: 0; }
        56%, 64% { opacity: 1; }
        72%, 100% { opacity: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
        .control-inspection [data-inspection-motion] { animation: none; }
        .control-inspection-detail { opacity: 0.65; }
        .control-inspection-frame { transform: translateX(${2 * PANEL_STEP}px); }
        .control-inspection-probe { display: none; }
    }
`;

export function ControlInspection(): React.ReactElement {
    const gradientId = React.useId();
    const figureRef = React.useRef<HTMLElement>(null);
    const inView = useInView(figureRef, { amount: 0.25 });
    const reducedMotion = useReducedMotion();
    const [pageVisible, setPageVisible] = React.useState(true);

    React.useEffect(() => {
        const updateVisibility = (): void => setPageVisible(!document.hidden);
        updateVisibility();
        document.addEventListener("visibilitychange", updateVisibility);
        return (): void =>
            document.removeEventListener("visibilitychange", updateVisibility);
    }, []);

    return (
        <figure
            ref={figureRef}
            className="control-inspection relative flex w-full max-w-122 min-w-0 flex-col"
            data-motion={
                inView && pageVisible && !reducedMotion ? "running" : "paused"
            }
            aria-label="Illustration of an inspection frame examining four connected program control schematics in sequence"
        >
            <style dangerouslySetInnerHTML={{ __html: INSPECTION_CSS }} />
            {/* The figure is the label room and the strip, exactly as the
                Blockaid one is, so the two end on one line and the descriptions
                under them start together. Stacked below lg the figure stops at
                30.5rem, as the Blockaid one does. */}
            <div className="overflow-hidden" style={{ marginTop: LABEL_ROOM }}>
                <svg
                    viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
                    className="block w-full"
                    fill="none"
                    aria-hidden="true"
                >
                    <defs>
                        {/* Same success gradient and opacity stops as Blockaid. */}
                        <linearGradient
                            id={gradientId}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="0"
                                stopColor="rgb(var(--color-success-base))"
                                stopOpacity="0.5"
                            />
                            <stop
                                offset="1"
                                stopColor="rgb(var(--color-success-base))"
                                stopOpacity="0.12"
                            />
                        </linearGradient>
                    </defs>
                    <path
                        d={`M0 ${RAIL_TOP_Y}H${VIEWBOX_WIDTH}M0 ${RAIL_BOTTOM_Y}H${VIEWBOX_WIDTH}`}
                        className="stroke-line-base"
                    />
                    <path
                        d={`M0 ${TRACK_Y}H${VIEWBOX_WIDTH}`}
                        className="stroke-line-base"
                        strokeDasharray="2 6"
                    />
                    {SCHEMATICS.map((schematic, index) => (
                        <g
                            key={schematic.path}
                            transform={`translate(${PANEL_FIRST + index * PANEL_STEP} ${TRACK_Y})`}
                        >
                            <rect
                                x={-16}
                                y={-16}
                                width={32}
                                height={32}
                                className="fill-surface-ground stroke-line-tint-neutral"
                            />
                            <path
                                d={schematic.path}
                                transform="scale(0.75)"
                                className="stroke-line-tint-neutral"
                            />
                            <g
                                className="control-inspection-detail text-tint-base"
                                data-inspection-motion
                                style={{
                                    animationDelay: `${index * SLOT_MS}ms`
                                }}
                            >
                                <rect
                                    x={-16}
                                    y={-16}
                                    width={32}
                                    height={32}
                                    fill="currentColor"
                                    fillOpacity={0.08}
                                    stroke="currentColor"
                                />
                                <path
                                    d={schematic.path}
                                    transform="scale(0.75)"
                                    stroke="currentColor"
                                />
                                <g transform="scale(0.75)">
                                    {schematic.nodes.map(([x, y]) => (
                                        <rect
                                            key={`${x},${y}`}
                                            x={x - 2}
                                            y={y - 2}
                                            width={4}
                                            height={4}
                                            className="fill-accent-base"
                                        />
                                    ))}
                                </g>
                            </g>
                        </g>
                    ))}
                    <g
                        className="control-inspection-frame text-tint-base"
                        data-inspection-motion
                    >
                        <g transform={`translate(${PANEL_FIRST} ${TRACK_Y})`}>
                            <rect
                                x={-FRAME_HALF}
                                y={-FRAME_HALF}
                                width={FRAME_HALF * 2}
                                height={FRAME_HALF * 2}
                                fill={`url(#${gradientId})`}
                                className="control-inspection-flash"
                                data-inspection-motion
                            />
                            <path
                                d="M-24 -32H-32V-24M24 -32H32V-24M-32 24V32H-24M32 24V32H24"
                                stroke="currentColor"
                            />
                            <path
                                d={`M${-FRAME_HALF} 0H${FRAME_HALF}`}
                                stroke="currentColor"
                                className="control-inspection-probe"
                                data-inspection-motion
                            />
                        </g>
                    </g>
                </svg>
            </div>
            {/* Right end of the strip, 0.25rem (gap-1) above the top rail, in
                the room LABEL_ROOM keeps above the strip, where the Blockaid
                strip carries its label. The frame's last stop stays left of it
                (PANEL_STEP). */}
            <Text
                variant="fine-print"
                render={<figcaption />}
                className="absolute right-0 text-foreground-neutral"
                style={{ bottom: LABEL_BOTTOM }}
            >
                Illustration
            </Text>
        </figure>
    );
}
